import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import AppErrorBoundary from './components/AppErrorBoundary';
import { Toaster } from './components/ui/sonner';
import ConnectivityBanner from './components/ConnectivityBanner';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RequestAccessPage from './pages/RequestAccessPage';
import DashboardLayout from './layouts/DashboardLayout';
import PracticeAdminDashboard from './pages/dashboards/PracticeAdminDashboard';
import PhysicianDashboard from './pages/dashboards/PhysicianDashboard';
import NurseDashboard from './pages/dashboards/NurseDashboard';
import BillingDashboard from './pages/dashboards/BillingDashboard';
import PatientPortal from './pages/dashboards/PatientPortal';
import SuperAdminDashboard from './pages/dashboards/SuperAdminDashboard';
import AideDashboard from './pages/dashboards/AideDashboard';
import PatientList from './pages/patients/PatientList';
import PatientProfile from './pages/patients/PatientProfile';
import AppointmentsPage from './pages/AppointmentsPage';
import MessagesPage from './pages/MessagesPage';

// New Pages - Phase 2
import EPrescribePage from './pages/prescriptions/EPrescribePage';
import ClinicalNotesEditor from './pages/notes/ClinicalNotesEditor';
import TelehealthRoom from './pages/telehealth/TelehealthRoom';
import SupportPage from './pages/SupportPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import PlatformSubscriptionsPage from './pages/admin/PlatformSubscriptionsPage';
import OrgSubscriptionPage from './pages/admin/OrgSubscriptionPage';
import ClaimsPage from './pages/billing/ClaimsPage';
import SelfSchedulePage from './pages/scheduling/SelfSchedulePage';

// Phase 3 Pages
import BrandingPage from './pages/admin/BrandingPage';
import PopulationHealthPage from './pages/analytics/PopulationHealthPage';
import ReportsPage from './pages/reports/ReportsPage';
import EHRIntegrationPage from './pages/ehr/EHRIntegrationPage';

// Phase 4 Pages
import EHRSettingsPage from './pages/ehr/EHRSettingsPage';
import WidgetBuilderPage from './pages/widgets/WidgetBuilderPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import SecuritySettingsPage from './pages/settings/SecuritySettingsPage';
import ProfileSettingsPage from './pages/settings/ProfileSettingsPage';

// Phase 5 Pages
import CQMDashboardPage from './pages/cqm/CQMDashboardPage';
import HomeCareDashboard from './pages/dashboards/HomeCareDashboard';
import FHIRWebhooksPage from './pages/ehr/FHIRWebhooksPage';

// Phase 6 Pages
import OrganizationManagementPage from './pages/admin/OrganizationManagementPage';
import ExecutiveInsightsPage from './pages/analytics/ExecutiveInsightsPage';
import CaseStudyPage from './pages/analytics/CaseStudyPage';
import ClaimsIntegrationPage from './pages/billing/ClaimsIntegrationPage';
import LiveClaimsTrackerPage from './pages/billing/LiveClaimsTrackerPage';
import DenialPreventionPage from './pages/billing/DenialPreventionPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage';
import DirectorPatientServicesDashboard from './pages/dashboards/DirectorPatientServicesDashboard';
import ServiceProviderDashboard from './pages/dashboards/ServiceProviderDashboard';
import ServiceRequestTypesPage from './pages/admin/ServiceRequestTypesPage';
import { ServiceRequestsInboxPage, ServiceRequestDetailPage } from './pages/admin/ServiceRequestsAdminPage';
import RoleLabelsAdminPage from './pages/admin/RoleLabelsAdminPage';
import BulkUploadsPage from './pages/admin/BulkUploadsPage';
import PatientTabsAdminPage from './pages/admin/PatientTabsAdminPage';
import PricingAdminPage from './pages/admin/PricingAdminPage';
import EVVDashboardPage from './pages/admin/EVVDashboardPage';
import CRMDashboard from './pages/crm/CRMDashboard';
import LeadsPage from './pages/crm/LeadsPage';
import CampaignsPage from './pages/crm/CampaignsPage';
import MarketingTemplatesPage from './pages/crm/MarketingTemplatesPage';
import MyChartIntegrationPage from './pages/ehr/MyChartIntegrationPage';
import FormsListPage from './pages/forms/FormsListPage';
import FormBuilderPage from './pages/forms/FormBuilderPage';
import FormFillPage from './pages/forms/FormFillPage';
import VoiceNotesPage from './pages/voice/VoiceNotesPage';
import PlatformInboxPage from './pages/admin/PlatformInboxPage';
import FormSubmissionEditPage from './pages/forms/FormSubmissionEditPage';
import SignaturePage from './pages/forms/SignaturePage';
import PrintOriginalView from './pages/forms/PrintOriginalView';
import SignOnPdfView from './pages/forms/SignOnPdfView';
import DueFormsBoard from './pages/forms/DueFormsBoard';
import FormLibraryPage from './pages/forms/FormLibraryPage';
import FormLayoutEditor from './pages/forms/FormLayoutEditor';
import RecertPacketPage from './pages/patients/RecertPacketPage';
import ModuleManagementPage from './pages/admin/ModuleManagementPage';
import CustomDashboardsPage from './pages/dashboards/CustomDashboardsPage';
import CustomDashboardView from './pages/dashboards/CustomDashboardView';
import TasksPage from './pages/tasks/TasksPage';
import LMSAdminPage from './pages/admin/LMSAdminPage';
import KnowledgeAdminPage from './pages/admin/KnowledgeAdminPage';
import ForumAdminPage from './pages/admin/ForumAdminPage';
import AccessRequestsAdminPage from './pages/admin/AccessRequestsAdminPage';
import AccessCodesPage from './pages/admin/AccessCodesPage';
import CsmWorkspacePage from './pages/admin/csm/CsmWorkspacePage';
import CsmTicketsPage from './pages/admin/csm/CsmTicketsPage';
import CsmTicketDetailPage from './pages/admin/csm/CsmTicketDetailPage';
import CsmConfigPage from './pages/admin/csm/CsmConfigPage';
import CsmAwaAdminPage from './pages/admin/csm/CsmAwaAdminPage';
import HRListPage from './pages/hr/HRListPage';
import HRDetailPage from './pages/hr/HRDetailPage';
import HROnboardingPage from './pages/hr/HROnboardingPage';
import AssetsWorkspacePage from './pages/assets/AssetsWorkspacePage';
import AssetsListPage from './pages/assets/AssetsListPage';
import AssetDetailPage from './pages/assets/AssetDetailPage';
import AssetNewPage from './pages/assets/AssetNewPage';
import AssetScanPage from './pages/assets/AssetScanPage';
import AssetLabelsPage from './pages/assets/AssetLabelsPage';
import CourseCatalogPage from './pages/learn/CourseCatalogPage';
import CoursePlayerPage from './pages/learn/CoursePlayerPage';
import KnowledgeBrowsePage from './pages/knowledge/KnowledgeBrowsePage';
import ArticleViewPage from './pages/knowledge/ArticleViewPage';
import ForumHomePage from './pages/forum/ForumHomePage';
import ForumCategoryPage from './pages/forum/ForumCategoryPage';
import ForumThreadPage from './pages/forum/ForumThreadPage';
import SubmitIncidentPage from './pages/incidents/SubmitIncidentPage';
import IncidentsListPage from './pages/incidents/IncidentsListPage';
import IncidentDetailPage from './pages/incidents/IncidentDetailPage';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#64748B]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Admin alias: mirror the backend `require_roles` behaviour so a
    // Director of Patient Services can reach anything a Practice Admin
    // can reach (without touching every route decorator).
    if (user.role === 'director_patient_services' && allowedRoles.includes('practice_admin')) {
      return children;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

import HomepageDashboardTab from './pages/dashboards/HomepageDashboardTab';
import LastLocationTracker from './components/LastLocationTracker';

// Dashboard Router - redirects to role-specific dashboard
function DashboardRouter() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = React.useState('main');

  const dashboardByRole = {
    super_admin: <SuperAdminDashboard />,
    practice_admin: <PracticeAdminDashboard />,
    physician: <PhysicianDashboard />,
    nurse: <NurseDashboard />,
    care_coordinator: <NurseDashboard />,
    aide: <AideDashboard />,
    billing_specialist: <BillingDashboard />,
    patient: <PatientPortal />,
    director_patient_services: <DirectorPatientServicesDashboard />,
    service_provider: <ServiceProviderDashboard />,
  };

  const roleDashboard = dashboardByRole[user?.role] || <PracticeAdminDashboard />;

  if (user?.role === 'patient') return roleDashboard;
  if (user?.role === 'aide') return roleDashboard;
  if (user?.role === 'service_provider') return roleDashboard;

  return (
    <div>
      <div className="flex border-b border-[#E2E8F0] mb-6">
        <button onClick={() => setActiveTab('main')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'main' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-main-dashboard">Overview</button>
        <button onClick={() => setActiveTab('custom')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'custom' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-custom-dashboard">My Dashboard</button>
      </div>
      {activeTab === 'main' ? roleDashboard : <HomepageDashboardTab />}
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/request-access" element={<RequestAccessPage />} />
      <Route path="/sign/:token" element={<SignaturePage />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardRouter />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/patients"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist']}>
            <DashboardLayout>
              <PatientList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/patients/:patientId"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist']}>
            <DashboardLayout>
              <PatientProfile />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AppointmentsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MessagesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* E-Prescribing */}
      <Route
        path="/prescriptions"
        element={
          <ProtectedRoute allowedRoles={['physician']}>
            <DashboardLayout>
              <EPrescribePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prescriptions/:patientId"
        element={
          <ProtectedRoute allowedRoles={['physician']}>
            <DashboardLayout>
              <EPrescribePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Clinical Notes */}
      <Route
        path="/voice-notes"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide']}>
            <DashboardLayout>
              <VoiceNotesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inbox"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <PlatformInboxPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notes"
        element={
          <ProtectedRoute allowedRoles={['physician']}>
            <DashboardLayout>
              <ClinicalNotesEditor />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notes/:encounterId"
        element={
          <ProtectedRoute allowedRoles={['physician']}>
            <DashboardLayout>
              <ClinicalNotesEditor />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Telehealth */}
      <Route
        path="/telehealth/join/:roomName"
        element={
          <ProtectedRoute>
            <TelehealthRoom />
          </ProtectedRoute>
        }
      />

      {/* Support/Tickets */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SupportPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Settings */}
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <AdminSettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Platform-wide subscription management (super_admin only) */}
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <DashboardLayout>
              <PlatformSubscriptionsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Dedicated org subscription page (redirects to the billing tab) */}
      <Route
        path="/subscription"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <OrgSubscriptionPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Claims/Billing */}
      <Route
        path="/claims"
        element={
          <ProtectedRoute allowedRoles={['billing_specialist', 'practice_admin']}>
            <DashboardLayout>
              <ClaimsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Public Self-Scheduling */}
      <Route path="/schedule" element={<SelfSchedulePage />} />

      {/* Branding */}
      <Route
        path="/admin/branding"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <BrandingPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Population Health */}
      <Route
        path="/analytics/population-health"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician']}>
            <DashboardLayout>
              <PopulationHealthPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Custom Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist']}>
            <DashboardLayout>
              <ReportsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* EHR Integration */}
      <Route
        path="/ehr"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse']}>
            <DashboardLayout>
              <EHRIntegrationPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* EHR Settings */}
      <Route
        path="/ehr/settings"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <EHRSettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Widget Builder */}
      <Route
        path="/widgets"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist']}>
            <DashboardLayout>
              <WidgetBuilderPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Audit Logs */}
      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <AuditLogPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Module Management */}
      <Route
        path="/admin/modules"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <DashboardLayout>
              <ModuleManagementPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* CQM Dashboard */}
      <Route
        path="/cqm"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician']}>
            <DashboardLayout>
              <CQMDashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Home Care Agency Dashboard */}
      <Route
        path="/home-care"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse']}>
            <DashboardLayout>
              <HomeCareDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* FHIR Webhooks */}
      <Route
        path="/fhir/webhooks"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <FHIRWebhooksPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Organization Management */}
      <Route
        path="/admin/organizations"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <DashboardLayout>
              <OrganizationManagementPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Executive Insights */}
      <Route
        path="/insights"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'care_coordinator']}>
            <DashboardLayout>
              <ExecutiveInsightsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Case Study */}
      <Route
        path="/case-study"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <CaseStudyPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Claims Integration */}
      <Route
        path="/claims/integration"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'billing_specialist']}>
            <DashboardLayout>
              <ClaimsIntegrationPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Live Claims Tracker */}
      <Route
        path="/claims/tracker"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'billing_specialist']}>
            <DashboardLayout>
              <LiveClaimsTrackerPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Denial Prevention Dashboard */}
      <Route
        path="/denials"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'billing_specialist']}>
            <DashboardLayout>
              <DenialPreventionPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* User Management */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <UserManagementPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <AdminUserDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Service Requests (Director / Service Provider) */}
      <Route
        path="/admin/service-request-types"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'director_patient_services']}>
            <DashboardLayout>
              <ServiceRequestTypesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/service-requests"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'director_patient_services']}>
            <DashboardLayout>
              <ServiceRequestsInboxPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/service-requests/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'director_patient_services', 'service_provider']}>
            <DashboardLayout>
              <ServiceRequestDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/role-labels"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'director_patient_services']}>
            <DashboardLayout>
              <RoleLabelsAdminPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bulk-uploads"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'director_patient_services']}>
            <DashboardLayout>
              <BulkUploadsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Patient Record Tabs admin */}
      <Route
        path="/admin/patient-tabs"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <PatientTabsAdminPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Pricing admin */}
      <Route
        path="/admin/pricing"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <DashboardLayout>
              <PricingAdminPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Access Requests — platform admin sees all, practice admin sees their org */}
      <Route
        path="/admin/access-requests"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <AccessRequestsAdminPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Access Codes — practice admin manages their org's codes */}
      <Route
        path="/admin/access-codes"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <AccessCodesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* CSM — support workspace */}
      <Route
        path="/admin/csm"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <CsmWorkspacePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/csm/tickets"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <CsmTicketsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/csm/tickets/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <CsmTicketDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/csm/config"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <DashboardLayout>
              <CsmConfigPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/csm/awa"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'csm_agent']}>
            <DashboardLayout>
              <CsmAwaAdminPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <HRListPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/:userId"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <HRDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/:userId/onboarding"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <HROnboardingPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Asset Management — order matters: specific paths before /:id */}
      <Route
        path="/assets"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'asset_manager']}>
            <DashboardLayout><AssetsWorkspacePage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/list"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'asset_manager']}>
            <DashboardLayout><AssetsListPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/new"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'asset_manager']}>
            <DashboardLayout><AssetNewPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/scan"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'asset_manager']}>
            <DashboardLayout><AssetScanPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/labels"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'asset_manager']}>
            <DashboardLayout><AssetLabelsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'asset_manager']}>
            <DashboardLayout><AssetDetailPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* EVV Dashboard (Home Care) */}
      <Route
        path="/admin/evv"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'nurse']}>
            <DashboardLayout>
              <EVVDashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* CRM Routes */}
      <Route
        path="/crm"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <CRMDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/leads"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <LeadsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/campaigns"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <CampaignsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/templates"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <MarketingTemplatesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* MyChart Integration */}
      <Route
        path="/mychart"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse']}>
            <DashboardLayout>
              <MyChartIntegrationPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Forms */}
      <Route
        path="/forms"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide', 'billing_specialist']}>
            <DashboardLayout>
              <FormsListPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/builder"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <DashboardLayout>
              <FormBuilderPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/:formId/fill"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide', 'billing_specialist', 'patient']}>
            <DashboardLayout>
              <FormFillPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/:formId/preview"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide', 'billing_specialist', 'patient']}>
            <DashboardLayout>
              <FormFillPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Form Submission Edit */}
      <Route
        path="/forms/:formId/submissions/:submissionId/edit"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse']}>
            <DashboardLayout>
              <FormSubmissionEditPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/forms/library"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'director_patient_services']}>
            <DashboardLayout><FormLibraryPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/due"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'nurse', 'care_coordinator', 'director_patient_services', 'physician']}>
            <DashboardLayout><DueFormsBoard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/:formId/layout-editor"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
            <FormLayoutEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/:patientId/recert-packet"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'director_patient_services', 'billing_specialist']}>
            <RecertPacketPage />
          </ProtectedRoute>
        }
      />

      {/* Print as Original Layout (PDF-imported forms) */}
      <Route
        path="/forms/:formId/print-original"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist', 'aide', 'care_coordinator', 'director_patient_services']}>
            <PrintOriginalView />
          </ProtectedRoute>
        }
      />

      {/* Sign on PDF (tablet/mobile interactive fill) */}
      <Route
        path="/forms/:formId/sign-on-pdf"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist', 'patient', 'aide', 'care_coordinator', 'director_patient_services']}>
            <SignOnPdfView />
          </ProtectedRoute>
        }
      />

      {/* Custom Dashboards */}
      <Route
        path="/my-dashboards"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist']}>
            <DashboardLayout>
              <CustomDashboardsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboards/:dashboardId"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'billing_specialist']}>
            <DashboardLayout>
              <CustomDashboardView />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Tasks */}
      <Route
        path="/tasks"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'aide', 'billing_specialist']}>
            <DashboardLayout>
              <TasksPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Security (MFA) — available to all authenticated users */}
      <Route
        path="/settings/security"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SecuritySettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Profile — all authenticated users */}
      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProfileSettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* LMS / Knowledge / Forum (admin authoring) */}
      <Route path="/admin/lms" element={
        <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
          <DashboardLayout><LMSAdminPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/knowledge" element={
        <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
          <DashboardLayout><KnowledgeAdminPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/forum" element={
        <ProtectedRoute allowedRoles={['super_admin', 'practice_admin']}>
          <DashboardLayout><ForumAdminPage /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* LMS / Knowledge / Forum (user-facing) */}
      <Route path="/learn" element={
        <ProtectedRoute><DashboardLayout><CourseCatalogPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/learn/courses/:courseId" element={
        <ProtectedRoute><DashboardLayout><CoursePlayerPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/knowledge" element={
        <ProtectedRoute><DashboardLayout><KnowledgeBrowsePage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/knowledge/:idOrSlug" element={
        <ProtectedRoute><DashboardLayout><ArticleViewPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/forum" element={
        <ProtectedRoute><DashboardLayout><ForumHomePage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/forum/c/:catId" element={
        <ProtectedRoute><DashboardLayout><ForumCategoryPage /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/forum/t/:threadId" element={
        <ProtectedRoute><DashboardLayout><ForumThreadPage /></DashboardLayout></ProtectedRoute>
      } />

      {/* Patient Incident Reports */}
      <Route path="/incidents" element={
        <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide']}>
          <DashboardLayout><IncidentsListPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/incidents/new" element={
        <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide']}>
          <DashboardLayout><SubmitIncidentPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/incidents/:id" element={
        <ProtectedRoute allowedRoles={['super_admin', 'practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide']}>
          <DashboardLayout><IncidentDetailPage /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <AppErrorBoundary>
            <ConnectivityBanner />
            <LastLocationTracker />
            <AppRoutes />
          </AppErrorBoundary>
          <Toaster position="top-right" richColors />
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
